// TODO(Sergio): remove this disabled rule
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { ANALYTICS_EVENTS } from '../data-models/constants/analytics-extended.constant';
import { triggerCustomEvent } from '../events/events.utils';
import { LoggingService } from '../logging/logging.service';
import {
  AdobeDataLayer,
  ClickEventAnalytics,
  FormEventAnalytics,
  PageLoadAnalytics,
  PageLoadErrorAnalytics
} from './analytics.interfaces';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private lastPageLoadEvent: string | null = null;
  private pageLoadTimestamp = 0;
  private readonly DUPLICATE_THRESHOLD_MS = 1000;
  constructor(private logging: LoggingService) {}

  dispatchCustomEvent<T>(eventName: string, detail: T) {
    triggerCustomEvent<T>(eventName, detail);
  }

  updateDataLayer<T>(
    property: string,
    data: T | string | undefined,
    path: string
  ) {
    try {
      const properties = path.split('.');
      const dataObject = properties.reduce(
        (prev: any, curr: any) => prev && prev[curr],
        window
      );
      if (dataObject) {
        dataObject[property] = data;
      }
    } catch (e) {
      this.logging.log('The data layer object is undefined');
    }
  }

  updateAdobeDataLayerAnalytics(data: AdobeDataLayer): void {
    (window as any).adobeDataLayer = (window as any).adobeDataLayer || [];
    (window as any).adobeDataLayer.push(data);
  }

  setAnalytics(
    analytics: PageLoadAnalytics | ClickEventAnalytics | PageLoadErrorAnalytics
  ): void {
    if (this.shouldSkipDuplicatePageLoad(analytics)) {
      return;
    }
    this.processEventByType(analytics);
    this.updateAdobeDataLayerAnalytics(analytics);
  }

  private shouldSkipDuplicatePageLoad(
    analytics: PageLoadAnalytics | ClickEventAnalytics | PageLoadErrorAnalytics
  ): boolean {
    if (analytics.event !== ANALYTICS_EVENTS.PAGE_LOAD) {
      return false;
    }
    const currentPageName = (analytics as PageLoadAnalytics).page?.info
      ?.page_name;
    const currentTime = Date.now();
    if (this.isDuplicatePageLoadEvent(currentPageName, currentTime)) {
      return true;
    }
    this.lastPageLoadEvent = currentPageName || null;
    this.pageLoadTimestamp = currentTime;
    return false;
  }

  private isDuplicatePageLoadEvent(
    currentPageName: string | undefined,
    currentTime: number
  ): boolean {
    return (
      !!currentPageName &&
      this.lastPageLoadEvent === currentPageName &&
      currentTime - this.pageLoadTimestamp < this.DUPLICATE_THRESHOLD_MS
    );
  }

  private processEventByType(
    analytics: PageLoadAnalytics | ClickEventAnalytics | PageLoadErrorAnalytics
  ): void {
    const options: Record<string, () => void> = {
      [ANALYTICS_EVENTS.PAGE_LOAD]: () => {
        if ((analytics as PageLoadErrorAnalytics).error == undefined) {
          const pageName = (analytics as PageLoadAnalytics).page.info.page_name;
          (
            analytics as PageLoadAnalytics
          ).page.info.page_name = `dal:${pageName}`;
        }
      },
      [ANALYTICS_EVENTS.CLICK]: () => {
        const pageName = (analytics as ClickEventAnalytics).click.page_section;
        (analytics as ClickEventAnalytics).click.page_section = `${pageName}`;
      },
      [ANALYTICS_EVENTS.ERROR]: () => {
        if ((analytics as PageLoadErrorAnalytics).error.type !== '') {
          const pageName = (analytics as PageLoadErrorAnalytics).page.info
            .page_name;
          (
            analytics as PageLoadErrorAnalytics
          ).page.info.page_name = `dal:${pageName}`;
        }
      }
    };

    const action =
      analytics.event === ANALYTICS_EVENTS.PAGE_LOAD
        ? ANALYTICS_EVENTS.PAGE_LOAD
        : analytics.event === ANALYTICS_EVENTS.CLICK
        ? ANALYTICS_EVENTS.CLICK
        : ANALYTICS_EVENTS.ERROR;
    if (options[action]) options[action]();
  }

  setFormAnalytics(analytics: FormEventAnalytics): void {
    this.updateAdobeDataLayerAnalytics(analytics);
  }
  removeCountryLanguageFromUrl(url: string): string {
    if (!url) return '';
    try {
      const removeCountryLangPattern = /^\/([a-z]{2,5})[-/]([a-z]{2})\/(.+)$/;
      const match = url.match(removeCountryLangPattern);
      return match ? `/${match[3]}` : url;
    } catch {
      return '';
    }
  }

  getCleanUrl(url: string): string {
    const removeDomainName = /^https?:\/\/[^/]+/;
    const fullUrl = url?.replace(removeDomainName, '') || '';
    return this.removeCountryLanguageFromUrl(fullUrl) || fullUrl;
  }
}
